/**
 * deliveryCheckExport.js — a Conferência de Entrega em Markdown (.md).
 *
 * Gerado dos MESMOS dados que a tela usa (resposta de /delivery-check + relatório de
 * garantia), nunca do DOM: mesmas seções, na mesma ordem, com os mesmos textos e os
 * mesmos ícones textuais. Tudo o que a tela abre ao clicar (linhas da matriz, grupos
 * da prova de dados, logs) sai aberto — o arquivo é a Conferência COMPLETA.
 *
 * A Auditoria de Entrega e a remediação estão OCULTAS na tela e por isso não entram.
 *
 * Módulo puro (sem React, sem DOM): testável em `deliveryCheckExport.test.js`.
 */
import { estadoDaPagina } from '@/components/OrigemDaEntrega';
import { NOTA_COMPLEMENTARES, contarGrupos, grupoDoItem, GRUPO_COMPLEMENTAR } from '@/components/GruposConferencia';
import { STATUS_PROVA, agruparPorEntidade } from '@/components/ProvaDeDadosSection';
import {
  ORDEM_STATUS_CONTRATO, NOTA_ESTATICO, rotuloStatus, agruparPorTela,
} from '@/components/ContratoFrontApiSection';
import { ORDEM_DONOS, ACAO, rotuloDoDono } from '@/components/ProblemasDaEntregaSection';
import {
  ROTULO_ESTADO, ROTULO_EIXO, ICONE, iconeDoCheck, rotuloDoCheck,
} from '@/components/EvidenciasSection';
import { ETAPAS, STATUS_RUNTIME, ESTAGIO } from '@/components/RuntimeSection';
import { TITULO_QUEM_FEZ, TITULO_DETERMINISTICO } from '@/api/relatorioDeGarantia';

// ── formatação ────────────────────────────────────────────────────────────────

const VAZIO = '—';

/** Número em pt-BR; qualquer coisa que não seja número finito vira "—" (nunca NaN). */
export function num(n) {
  const v = Number(n);
  return Number.isFinite(v) ? v.toLocaleString('pt-BR') : VAZIO;
}

/** Número finito ou 0 (para contas). */
function n0(n) {
  const v = Number(n);
  return Number.isFinite(v) ? v : 0;
}

/** Texto de uma linha (fora de tabela): vazio vira "—". */
function txt(v) {
  if (v === null || v === undefined) return VAZIO;
  const s = String(v).trim();
  return s ? s : VAZIO;
}

/** Célula de tabela Markdown: escapa `|`, quebra de linha vira <br>, vazio vira "—". */
export function celula(v) {
  if (v === null || v === undefined) return VAZIO;
  const s = String(v).replace(/\r\n?/g, '\n').trim();
  if (!s) return VAZIO;
  return s.replace(/\\/g, '\\\\').replace(/\|/g, '\\|').replace(/\n/g, '<br>');
}

/** Tabela Markdown a partir de cabeçalho + linhas (cada célula passa por `celula`). */
export function tabela(cabecalho, linhas) {
  const out = [
    `| ${cabecalho.map(celula).join(' | ')} |`,
    `| ${cabecalho.map(() => '---').join(' | ')} |`,
  ];
  for (const l of linhas) out.push(`| ${cabecalho.map((_, i) => celula(l[i])).join(' | ')} |`);
  return out.join('\n');
}

/** Bloco de código com cerca maior que qualquer sequência de crases do conteúdo. */
function blocoDeCodigo(conteudo) {
  const s = String(conteudo ?? '');
  const maior = Math.max(2, ...(s.match(/`+/g) ?? []).map((m) => m.length));
  const cerca = '`'.repeat(maior + 1);
  return `${cerca}\n${s}\n${cerca}`;
}

/** Barra de progresso textual (0–100). */
function barra(pct) {
  const p = Math.max(0, Math.min(100, n0(pct)));
  const cheios = Math.round(p / 5);
  return `\`${'█'.repeat(cheios)}${'░'.repeat(20 - cheios)}\``;
}

/** AAAA-MM-DD no fuso local (nome do arquivo). */
export function dataDoArquivo(d = new Date()) {
  const p2 = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
}

/** Data e hora em pt-BR (cabeçalho dos arquivos). */
export function dataHoraPtBR(d = new Date()) {
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

/** Nome do arquivo exportado: conferencia-<slug>-<AAAA-MM-DD>.<ext>. */
export function nomeDoArquivo(slug, ext, d = new Date()) {
  return `conferencia-${slug}-${dataDoArquivo(d)}.${ext}`;
}

const ST = {
  atendido: 'ATENDIDO',
  parcial: 'PARCIAL',
  nao_atendido: 'NÃO ATENDIDO',
};

const SELO = {
  READY_FOR_REVIEW: '🟢 Pronta para revisão',
  HUMAN_REVIEW_REQUIRED: '🟠 Revisão humana',
  READY_FOR_CLIENT_FINALIZATION: '🔵 Pronta para finalização no cliente',
};

function fileBase(name = '') {
  const parts = String(name).split('/');
  return parts[parts.length - 1] || name;
}

const arr = (v) => (Array.isArray(v) ? v : []);

// ── métricas (as mesmas contas da tela) ──────────────────────────────────────

/** As contas do cabeçalho da Conferência, idênticas às de DeliveryCheckScreen. */
export function metricasDaConferencia(data) {
  const rows = arr(data?.rows);
  const design = arr(data?.design);
  const okReqs = rows.filter((r) => r.status === 'atendido').length;
  const divergences = rows.filter((r) => r.status !== 'atendido' || r.divergence).length;
  const criteriaOk = n0(data?.criteriaOk);
  const criteriaTotal = n0(data?.criteriaTotal);
  const score = n0(criteriaTotal > 0 ? (data?.coveragePct ?? 0) : (data?.verdictPct ?? 0));
  const okScreens = design.filter((d) => d.ok).length;
  const screensTotal = n0(data?.screensTotal ?? design.length);
  const screensOk = n0(data?.screensOk ?? okScreens);
  const designPct = n0(data?.designCoveragePct
    ?? (screensTotal ? Math.round((100 * screensOk) / screensTotal) : 0));
  return {
    rows, design, okReqs, divergences, criteriaOk, criteriaTotal, score, okScreens,
    screensTotal, screensOk, designPct,
  };
}

// ── seções ────────────────────────────────────────────────────────────────────

function secaoOrigem(entrega, analise) {
  if (!entrega || analise) return [];
  const st = estadoDaPagina(entrega);
  const id8 = String(entrega.executionId || '').slice(0, 8);
  const out = [];
  if (entrega.emAndamento) {
    out.push(`> ⏳ A execução **${id8}** está em andamento. Conferência e Auditoria ficam disponíveis `
      + 'quando ela terminar — antes disso elas comparariam uma entrega pela metade.');
  }
  if (!entrega.emAndamento && !st.enviada) {
    out.push(`> 📦 Esta página lê a **entrega pendente** da execução **${id8}**, ainda **não enviada ao `
      + 'GitHub**. Conferir e auditar **não precisam de Pull Request**. O PR só é necessário para o '
      + '**Gate Final**, que revisa o commit.');
  }
  if (!entrega.emAndamento && st.enviada) {
    const pr = entrega.prUrl
      ? ` — [Pull Request${entrega.prNumber ? ` #${entrega.prNumber}` : ''}](${entrega.prUrl})` : '';
    out.push(`> 🔗 Entrega enviada ao GitHub${pr}. Conferência e Auditoria leem o que foi enviado.`);
  }
  if (st.desatualizada) {
    out.push(`> ⚠ A matriz do QA abaixo é de uma **execução anterior**. Clique em **Reconferir** para `
      + `conferir a execução ${id8}.`);
  }
  if (!out.length) return [];
  return ['## Origem da entrega', '', out.join('\n>\n'), ''];
}

function secaoVereditoPlataforma(assurance, analise) {
  if (analise || !assurance) return [];
  const reqs = arr(assurance.requisitos);
  const provados = reqs.filter((r) => r.estado === 'provado').length;
  const selo = SELO[assurance.estado];
  if (assurance.delivery_mode === 'client' || assurance.estado === 'READY_FOR_CLIENT_FINALIZATION') {
    return [
      '## Veredito da plataforma', '',
      `${selo ? `**${selo}** — ` : ''}A plataforma não executou o código desta entrega. As provas foram `
        + 'escritas e serão executadas na finalização, a partir do FINALIZAR.md da entrega. O percentual '
        + 'abaixo é a leitura do QA pelos nomes dos arquivos.',
      '',
    ];
  }
  return [
    '## Veredito da plataforma', '',
    `${selo ? `**${selo}** — ` : ''}${num(provados)} de ${num(reqs.length)} requisito(s) provado(s) executando `
      + 'o código (implementação · testes · runtime). É este veredito que vale para o aceite — o percentual '
      + 'abaixo é a leitura do QA pelos nomes dos arquivos.',
    '',
  ];
}

function secaoVeredito(data, m, analise) {
  const out = [
    `## Aderência — ${num(m.score)}%`, '',
    `**${num(m.score)}%** — Aderência da entrega ao que foi aprovado nos Gates de Negócio e Tecnologia`, '',
    `${barra(m.score)} ${num(m.score)}%`, '',
    `${m.criteriaTotal > 0
      ? `${num(m.criteriaOk)} de ${num(m.criteriaTotal)} critérios de aceite verificados com evidência`
      : `${num(m.okReqs)} de ${num(m.rows.length)} requisitos atendidos`} · leitura do qa-expert (via Claude) `
      + 'pelos nomes dos arquivos — não executa o código',
    '',
  ];
  if (analise && n0(data?.nfrTotal) > 0) {
    out.push(`> 📐 Cobertura de **conteúdo** (o que a análise entrega). **${num(data.nfrDocumented ?? 0)}/`
      + `${num(data.nfrTotal)}** requisitos de processo/NFR (SLA, precisão, runtime) são documentados via `
      + 'metodologia/limitações — contados à parte, pois não são entregáveis por análise estática.', '');
  }
  return out;
}

function secaoCoberturaDeTelas(m, analise) {
  if (analise || !(m.screensTotal > 0)) return [];
  return [
    `## Cobertura de telas — ${num(m.designPct)}% · ${num(m.screensOk)}/${num(m.screensTotal)} telas`, '',
    `**${num(m.designPct)}%** · **${num(m.screensOk)}/${num(m.screensTotal)} telas** do design aprovado têm UI `
      + 'navegável entregue', '',
    `${barra(m.designPct)} ${num(m.designPct)}%`, '',
    `A aderência de **${num(m.score)}%** acima mede os **requisitos** aprovados — que são um *resumo* do design. `
      + 'Esta barra mede as **telas** que o cliente aprovou no Design Studio. Quando os dois números divergem '
      + 'muito, há telas planejadas que ainda não viraram UI.',
    '',
  ];
}

function secaoKpis(m) {
  const linhas = [
    ['Requisitos atendidos', `${num(m.okReqs)}/${num(m.rows.length)}`, 'aprovados no Gate de Negócio'],
    ['Critérios verificados', m.criteriaTotal ? `${num(m.criteriaOk)}/${num(m.criteriaTotal)}` : VAZIO,
      'com evidência vinculada'],
  ];
  if (m.design.length > 0) {
    linhas.push(['Telas do design', `${num(m.okScreens)}/${num(m.design.length)}`, 'design vs. implementado']);
  }
  linhas.push(['Divergências', num(m.divergences), m.divergences ? 'requer decisão humana' : 'nenhuma']);
  return ['## Indicadores', '', tabela(['Indicador', 'Valor', 'Detalhe'], linhas), ''];
}

function linhaDaMatriz(r, i, analise) {
  const crit = arr(r.criteria);
  const files = arr(r.files);
  const tests = arr(r.tests);
  const isOk = r.status === 'atendido';
  const status = `${isOk ? '✓' : '!'} ${ST[r.status] || ST.nao_atendido}`;
  const criterios = crit.length
    ? crit.map((c) => `${c.ok ? '✓' : '✗'} ${txt(c.text)}${c.evidence ? ` — Evidência: ${c.evidence}` : ''}`)
      .join('\n')
    : (r.delivered ? String(r.delivered) : '');
  const arquivos = files.length
    ? files.map((f) => `📄 ${f.name || fileBase(f.name)}${f.changes ? ` ${f.changes}` : ''}`
      + ` (${f.type === 'A' ? 'criado' : 'modificado'})`).join('\n')
    : arr(r.evidence).join('\n');
  let testes;
  if (tests.length) testes = tests.map((t) => `🧪 ${txt(t.name)} · ${t.ok ? 'passou' : 'falhou'}`).join('\n');
  else testes = analise ? '' : '🧪 Testes: não conferido — sem relatório de execução';
  const div = r.divergence && (r.divergence.text || r.divergence.action)
    ? `⚠ Divergência${r.divergence.severity ? ` (${r.divergence.severity})` : ''}: ${r.divergence.text ?? ''}`
      + `${r.divergence.action ? `\nAção recomendada: ${r.divergence.action}` : ''}`
    : '';
  return [String(i + 1), r.requirement, status, criterios, arquivos, testes,
    r.screen ? `🎨 ${r.screen}` : '', div];
}

function secaoMatriz(m, analise) {
  return [
    '## ▦ Matriz de rastreabilidade — requisito → evidência', '',
    'Cadeia completa de cada requisito: critérios de aceite, arquivos gerados, testes que comprovam e a tela '
      + 'do design que atende.',
    '',
    tabela(['#', 'Requisito', 'Status', 'Critérios de aceite · pedido vs. entregue', 'Arquivos',
      'Testes', 'Tela', 'Divergência'], m.rows.map((r, i) => linhaDaMatriz(r, i, analise))),
    '',
  ];
}

function chipsDeGrupo(contagem, rotuloOk) {
  const ok = n0(contagem?.ok);
  const compl = n0(contagem?.complementares);
  const corr = n0(contagem?.correcoes);
  return [
    `\`${num(ok)} ${rotuloOk}\``,
    `\`${num(compl)} ${compl === 1 ? 'atividade complementar' : 'atividades complementares'}\``,
    `\`${num(corr)} ${corr === 1 ? 'correção pendente' : 'correções pendentes'}\``,
  ].join(' · ');
}

function rotuloProva(status) {
  return (STATUS_PROVA[status] ?? { label: status || 'desconhecido' }).label;
}

function secaoProvaDeDados(prova) {
  if (!prova) return [];
  const itens = arr(prova.itens);
  const total = n0(prova.total ?? itens.length);
  const porStatus = prova.porStatus ?? {};
  const provadas = n0(porStatus.passed ?? itens.filter((i) => i.status === 'passed').length);
  const porGrupo = prova.porGrupo ?? contarGrupos(itens);
  const out = [
    '## ⛁ Prova de dados · entidade × operação × regra', '',
    `**${num(provadas)} de ${num(total)} obrigações provadas contra o banco real**`, '',
    chipsDeGrupo(porGrupo, 'provadas'), '',
  ];
  if (n0(porGrupo.complementares) > 0) out.push(NOTA_COMPLEMENTARES, '');
  const chips = ['passed', 'failed', 'missing', 'nao_usou_banco', 'nao_conferido', 'trivial']
    .filter((s) => n0(porStatus[s]) > 0).map((s) => `\`${num(porStatus[s])} ${rotuloProva(s)}\``);
  if (chips.length) out.push(chips.join(' · '), '');
  out.push('Status vindo da execução real da suíte de integração contra o banco do blueprint — não da IA.', '');
  const grupos = agruparPorEntidade(itens);
  if (!grupos.length) {
    out.push('Nenhuma obrigação registrada.', '');
    return out;
  }
  for (const [nome, lista] of grupos) {
    const ok = lista.filter((i) => i.status === 'passed').length;
    out.push(`### ${nome} — ${num(ok)}/${num(lista.length)} provadas`, '');
    out.push(tabela(['Obrigação', 'Status', 'Tipo', 'Escritas', 'Testes', 'Repositório', 'Evidência'],
      lista.map((it) => [
        it.id,
        `${rotuloProva(it.status)}${grupoDoItem(it) === GRUPO_COMPLEMENTAR ? ' · atividade complementar' : ''}`,
        it.kind,
        `${num(it.dbWrites ?? 0)} escrita(s)`,
        arr(it.tests).length ? arr(it.tests).join(', ') : 'nenhum teste associado',
        it.repo,
        it.status !== 'passed' ? it.evidence : '',
      ])), '');
  }
  return out;
}

function secaoContrato(matriz) {
  if (!matriz) return [];
  const itens = arr(matriz.itens);
  const modo = matriz.modo ?? 'estatico';
  const porSuperficie = matriz.porSuperficie ?? {};
  const porGrupoSuperficie = matriz.porGrupoSuperficie ?? null;
  const porGrupo = matriz.porGrupo ?? contarGrupos(itens);
  const nomeSup = (s) => ({ web: 'Web', mobile: 'Mobile' }[s] ?? s);
  const ordemSup = ['web', 'mobile'];
  const superficies = [...new Set([...Object.keys(porSuperficie), ...itens.map((i) => i.surface).filter(Boolean)])]
    .sort((a, b) => {
      const ia = ordemSup.indexOf(a); const ib = ordemSup.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b, 'pt-BR');
    });
  const out = ['## ⇄ Contrato front ↔ API por superfície', ''];
  const comContagem = superficies.filter((s) => porSuperficie[s]);
  if (!comContagem.length) out.push('Nenhuma superfície registrada.', '');
  for (const s of comContagem) {
    const partes = ORDEM_STATUS_CONTRATO.map((st) => {
      const n = n0(porSuperficie[s]?.[st]);
      return `${num(n)} ${rotuloStatus(st, modo)}${st !== 'ok' && n !== 1 ? 's' : ''}`;
    }).join(' · ');
    out.push(`- **${nomeSup(s)}:** ${partes}`
      + `${porGrupoSuperficie?.[s] ? ` — ${chipsDeGrupo(porGrupoSuperficie[s], rotuloStatus('ok', modo))}` : ''}`);
  }
  if (comContagem.length) out.push('');
  out.push(chipsDeGrupo(porGrupo, rotuloStatus('ok', modo)), '');
  if (n0(porGrupo.complementares) > 0) out.push(NOTA_COMPLEMENTARES, '');
  if (modo === 'estatico') out.push(NOTA_ESTATICO, '');
  const grupos = agruparPorTela(itens);
  if (!grupos.length) {
    out.push('Nenhuma funcionalidade registrada.', '');
    return out;
  }
  const varias = superficies.length > 1;
  for (const [nome, lista] of grupos) {
    const ok = lista.filter((i) => i.status === 'ok').length;
    out.push(`### ${nome} — ${num(ok)}/${num(lista.length)} ok`, '');
    const cab = ['Funcionalidade', 'Ação', 'Endpoint', ...(varias ? ['Superfície'] : []), 'Status', 'Evidência'];
    out.push(tabela(cab, lista.map((it) => {
      const compl = grupoDoItem(it) === GRUPO_COMPLEMENTAR;
      return [
        it.featureRef, it.action, `${it.method ?? ''} ${it.path ?? ''}`.trim(),
        ...(varias ? [nomeSup(it.surface)] : []),
        `${rotuloStatus(it.status, modo)}${compl ? ' · atividade complementar do backend' : ''}`,
        it.status !== 'ok' ? it.evidence : '',
      ];
    })), '');
  }
  return out;
}

function secaoProblemas(manifest) {
  if (!manifest) return [];
  const itens = arr(manifest.itens);
  const out = [
    `## Problemas da entrega (${num(itens.length)})`, '',
    'Cada item diz quem corrige, o que fazer e a qual requisito se liga — os mesmos itens de '
      + 'CORRECOES-PENDENTES.md e ATIVIDADES-COMPLEMENTARES.md da entrega.',
    '',
  ];
  if (!itens.length) {
    out.push('Nenhum problema aberto na entrega mais recente.', '');
    return out;
  }
  const porDono = ORDEM_DONOS.filter((d) => manifest.porDono?.[d])
    .map((d) => `${rotuloDoDono(d).split(' · ')[0]} (${num(manifest.porDono[d])})`);
  out.push(`Todos (${num(itens.length)})${porDono.length ? ` · ${porDono.join(' · ')}` : ''}`, '');
  const grupos = new Map();
  for (const g of itens) {
    const chave = rotuloDoDono(g.ownerAgent);
    if (!grupos.has(chave)) grupos.set(chave, []);
    grupos.get(chave).push(g);
  }
  const ordenados = [...grupos.entries()].sort((a, b) => {
    const ia = ORDEM_DONOS.indexOf(a[1][0].ownerAgent); const ib = ORDEM_DONOS.indexOf(b[1][0].ownerAgent);
    return ia - ib;
  });
  for (const [chave, lista] of ordenados) {
    out.push(`### ${chave} (${num(lista.length)})`, '');
    for (const g of lista) {
      const meta = [
        `**${String(ACAO[g.action] ?? g.action ?? VAZIO).toUpperCase()}**`,
        g.category ? `\`${g.category}\`` : null,
        rotuloDoDono(g.ownerAgent),
        g.sourceRequirement ? `\`${g.sourceRequirement}\`` : null,
        g.severity === 'aviso' ? 'aviso' : null,
        g.severity === 'bloqueio' ? '⛔ bloqueio' : null,
      ].filter(Boolean).join(' · ');
      out.push(`- ${meta} — ${txt(g.message)}`);
      for (const ev of arr(g.evidence)) out.push(`  - Evidência: ${ev}`);
      if (g.log) {
        out.push('', '  Log da subida da stack:', '');
        out.push(blocoDeCodigo(g.log).split('\n').map((l) => `  ${l}`).join('\n'));
      }
    }
    out.push('');
  }
  return out;
}

function eixo(e) {
  if (!e?.status) return VAZIO;
  return `${ICONE[e.status] ?? ''} ${ROTULO_EIXO[e.status] ?? e.status}`.trim()
    + (arr(e.evidencia).length ? `\n${arr(e.evidencia).join('\n')}` : '');
}

function secaoEvidencias(assurance) {
  if (!assurance) return [];
  const reqs = arr(assurance.requisitos);
  const provados = reqs.filter((r) => r.estado === 'provado').length;
  const selo = SELO[assurance.estado];
  const out = [
    `## Evidências por requisito${selo ? ` — ${selo}` : ''}`, '',
    `${num(provados)} de ${num(reqs.length)} requisito(s) provado(s) — implementação, testes e runtime. O mesmo `
      + 'conteúdo sai em reports/software-assurance-report.md na entrega.',
    '',
  ];
  for (const r of arr(assurance.repos)) {
    const partes = [r.repo ? `\`${r.repo}\`` : null, SELO[r.estado] ?? null];
    if (r.metricas) {
      partes.push(`problemas — antes da geração: ${num(r.metricas.pre ?? 0)} · na geração: `
        + `${num(r.metricas.geracao ?? 0)} · no runtime: ${num(r.metricas.runtime ?? 0)}`);
    }
    out.push(`- ${partes.filter(Boolean).join(' · ') || VAZIO}`);
    for (const mtv of arr(r.motivos)) out.push(`  - ${mtv}`);
  }
  if (arr(assurance.repos).length) out.push('');
  out.push(tabela(['Requisito', 'Estado', 'Implementação', 'Testes', 'Runtime'], reqs.map((r) => [
    `${r.codigo ?? ''} ${r.titulo ?? ''}`.trim(), ROTULO_ESTADO[r.estado] ?? r.estado,
    eixo(r.implementacao), eixo(r.testes), eixo(r.runtime),
  ])), '');
  const checks = arr(assurance.checks);
  if (checks.length) {
    out.push('### CI do cliente (checks do PR)', '');
    for (const k of checks) {
      out.push(`- ${iconeDoCheck(k)} ${txt(k.nome)} — ${rotuloDoCheck(k)}${k.resumo ? ` · ${k.resumo}` : ''}`);
    }
    out.push('');
  }
  return out;
}

function secaoQuemFez(relatorio) {
  if (!relatorio) return [];
  const { colunas = [], linhas = [], deterministico = '', deterministicos = [] } = relatorio;
  const out = [
    `## ${TITULO_QUEM_FEZ}`, '',
    'Lido do relatório de garantia da entrega (reports/software-assurance-report.md): cada etapa, o agente '
      + 'que a executou, a versão do prompt, o tier, o modelo resolvido, o custo, as delegações a subagentes e '
      + 'os achados das revisões.',
    '',
  ];
  if (arr(linhas).length > 0 && arr(colunas).length > 0) out.push(tabela(colunas, linhas), '');
  else {
    out.push('O relatório desta entrega não traz o quadro de agentes (entrega anterior aos agentes '
      + 'especialistas, ou o quadro não pôde ser montado).', '');
  }
  if (deterministico || arr(deterministicos).length) {
    out.push(`### ${TITULO_DETERMINISTICO}`, '');
    if (deterministico) out.push(deterministico, '');
    if (arr(deterministicos).length) out.push(...deterministicos.map((d) => `- ${d}`), '');
  }
  return out;
}

function secaoRuntime(runtime) {
  if (!runtime) return [];
  const componentes = arr(runtime.componentes);
  const out = ['## Runtime', '', txt(ESTAGIO[runtime.estagio] ?? runtime.estagio), ''];
  const logs = [];
  const cel = (c, k) => {
    const e = c.etapas?.[k];
    if (!e) return VAZIO;
    const [rotulo] = STATUS_RUNTIME[e.status] ?? [e.status];
    if (e.log) logs.push([c, k, e.log]);
    return `${rotulo}${e.evidencia ? `\n${e.evidencia}` : ''}`;
  };
  out.push(tabela(['Componente', ...ETAPAS.map(([, r]) => r)], componentes.map((c) => [
    `${c.componente === 'compose' ? 'stack (docker compose)' : c.componente}\n${c.kind ?? ''}`
      + `${c.repo ? ` · ${c.repo}` : ''}`,
    ...ETAPAS.map(([k]) => cel(c, k)),
  ])), '');
  for (const [c, k, log] of logs) {
    const etapa = (ETAPAS.find(([kk]) => kk === k) ?? [k, k])[1];
    out.push(`**Log — ${c.componente === 'compose' ? 'stack (docker compose)' : c.componente} · ${etapa}**`, '');
    out.push(blocoDeCodigo(log), '');
  }
  return out;
}

function secaoDesign(design) {
  if (!design.length) return [];
  return [
    '## 🎨 Conferência do design — planejado vs. implementado', '',
    tabela(['', 'Tela', 'Planejado', '→ Entregue'],
      design.map((d) => [d.ok ? '✓' : '✗', d.screen, d.planned, d.delivered])),
    '',
  ];
}

function secaoAceite(m, analise, pagina, comentario) {
  const out = ['## Aceite', ''];
  if (analise && m.score < 100) {
    out.push('> ⚠ A análise ainda não cobre 100% do solicitado. Recomendado **corrigir as lacunas e '
      + 'reprocessar** acima antes de concluir no Gate Final. Se aceitar assim mesmo, registre a justificativa '
      + 'abaixo.', '');
  }
  if (comentario && String(comentario).trim()) {
    out.push('**Justificativa / observações do aceite:**', '', `> ${String(comentario).trim()
      .replace(/\r?\n/g, '\n> ')}`, '');
  }
  out.push('🛡 O aceite formal acontece no **Gate Final** e gera evento imutável na trilha de auditoria '
    + '(aderência, divergências e justificativas).', '');
  if (!pagina.podeAceitar && pagina.motivoAceitar) out.push(`🔒 ${pagina.motivoAceitar}`, '');
  return out;
}

/**
 * A Conferência inteira em Markdown.
 * @param {object} p
 * @param {string} [p.projeto]   nome do projeto
 * @param {object} p.data        resposta de /delivery-check (a mesma da tela)
 * @param {boolean} [p.analise]  projeto de análise (delivery_scope = analise)
 * @param {object|null} [p.quemFez] relatório de garantia reduzido (useRelatorioDeGarantia)
 * @param {string} [p.comentario] justificativa digitada na tela
 * @param {Date} [p.geradoEm]
 * @returns {string}
 */
export function conferenciaParaMarkdown({
  projeto, data, analise = false, quemFez = null, comentario = '', geradoEm = new Date(),
} = {}) {
  const m = metricasDaConferencia(data);
  const pagina = estadoDaPagina(data?.entrega, { analise });
  const out = [
    '# ✔ Conferência de Entrega', '',
    `**Projeto:** ${txt(projeto)} · **Exportado em:** ${dataHoraPtBR(geradoEm)}`, '',
  ];
  if (m.rows.length > 0) {
    out.push(`\`${m.divergences
      ? `${num(m.divergences)} DIVERGÊNCIA${m.divergences !== 1 ? 'S' : ''}` : 'ADERÊNCIA TOTAL'}\``, '');
  }
  out.push(
    'Matriz "pedido (requisitos) vs. entregue (arquivos)" gerada pelo QA (via AI Core/Claude), mapeando os '
      + 'requisitos reais contra os artefatos reais — critérios de aceite com evidência, testes e telas. Aceite '
      + 'humano antes do **Gate Final**.',
    '',
    ...secaoOrigem(data?.entrega, analise),
    '> **Quando rodar:** depois que a squad concluiu a execução e **entregou o código** (requisitos extraídos + '
      + 'artefatos gerados). É o passo final de verificação antes do Gate Final — compara o que foi pedido com o '
      + 'que foi entregue.',
    '',
  );
  if (m.rows.length === 0) {
    out.push('Sem conferência ainda. Após a execução entregar o código, clique em **Conferir entrega com IA**.', '');
    return `${out.join('\n').trimEnd()}\n`;
  }
  if (data?.aiComplete === false) {
    out.push('> ⚠ Este retorno da IA foi CORTADO (truncado por limite de tokens) — o conteúdo pode estar '
      + 'incompleto.', '');
  }
  out.push(
    ...secaoVereditoPlataforma(data?.assurance, analise),
    ...secaoVeredito(data, m, analise),
    ...secaoCoberturaDeTelas(m, analise),
  );
  if (analise && m.score >= 100) {
    out.push('✓ Análise cobre 100% do solicitado — pronta para concluir no Gate Final.', '');
  }
  out.push(...secaoKpis(m), ...secaoMatriz(m, analise));
  if (!analise) {
    out.push(
      ...secaoProvaDeDados(data?.provaDeDados),
      ...secaoContrato(data?.featureMatrix),
      ...secaoProblemas(data?.repairManifest),
      ...secaoEvidencias(data?.assurance),
      ...(data?.assurance?.executionId ? secaoQuemFez(quemFez) : []),
      ...secaoRuntime(data?.runtime),
    );
  }
  out.push(...secaoDesign(m.design), ...secaoAceite(m, analise, pagina, comentario));
  return `${out.join('\n').trimEnd()}\n`;
}
